import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Mail, CheckCircle } from 'lucide-react';

interface FormData {
  name: string;
  email: string;
  phone: string;
  message: string;
}

const Contact = () => {
  const [formData, setFormData] = useState<FormData>({
    name: '',
    email: '',
    phone: '',
    message: '',
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      const response = await fetch('https://hook.eu2.make.com/q2os969dkhronovbdjb1mx10ojoycioe', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(formData),
      });

      if (response.ok) {
        setShowSuccess(true);
        setTimeout(() => {
          setShowSuccess(false);
          setFormData({ name: '', email: '', phone: '', message: '' });
        }, 3000);
      } else {
        throw new Error('Failed to submit form');
      }
    } catch (error) {
      alert('Sorry, there was an error submitting your form. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <section id="contact" className="section-padding relative overflow-hidden">
      <div className="container mx-auto px-4 md:px-6 relative z-10">
        <div className="max-w-4xl mx-auto">
          <div className="text-center mb-12">
            <h1 id="contact-heading" className="text-4xl md:text-5xl lg:text-6xl font-bold mb-4 mt-0 text-neutral">
              Get in touch with us
            </h1>
            <p className="text-lg md:text-xl text-neutral/80 mt-4">
              Our team looks forward to helping you explore how to catalyze your growth. Let's start a conversation about your goals.
            </p>
          </div>

          <div>
            <div className="bg-[#111]/95 backdrop-blur-lg rounded-xl p-8 border border-accent/20">
              {/* Contact Info */}
              <div className="flex flex-col items-center text-center mb-4">
                <Mail size={40} className="text-accent mb-4" />
                <h3 className="text-2xl font-semibold mb-3 text-neutral">Contact Information</h3>
                <p className="text-neutral/80 mb-2">
                  Have questions or ready to start a conversation? Reach out to our team directly.
                </p>
                <p className="text-neutral mb-4">
                  Email us at{' '}
                  <a 
                    href="mailto:info@quantumsphere.ae"
                    className="text-neutral hover:text-neutral/80 transition-colors duration-300 underline font-bold"
                  >
                    info@quantumsphere.ae
                  </a>
                </p>
              </div>

              {/* Contact Form */}
              <AnimatePresence mode="wait">
                {!showSuccess ? (
                  <form
                    onSubmit={handleSubmit}
                    className="space-y-4 max-w-2xl mx-auto"
                  >
                    <div>
                      <label htmlFor="name" className="block text-sm font-medium text-neutral/80 mb-1">
                        Your Name <span className="text-accent">*</span>
                      </label>
                      <input
                        type="text"
                        id="name"
                        name="name"
                        value={formData.name}
                        onChange={handleChange}
                        required
                        className="form-input w-full"
                        aria-required="true"
                      />
                    </div>

                    <div>
                      <label htmlFor="email" className="block text-sm font-medium text-neutral/80 mb-1">
                        Email Address <span className="text-accent">*</span>
                      </label>
                      <input
                        type="email"
                        id="email"
                        name="email"
                        value={formData.email}
                        onChange={handleChange}
                        required
                        className="form-input w-full"
                        aria-required="true"
                      />
                    </div>

                    <div>
                      <label htmlFor="phone" className="block text-sm font-medium text-neutral/80 mb-1">
                        Phone Number
                      </label>
                      <input
                        type="tel"
                        id="phone"
                        name="phone"
                        value={formData.phone}
                        onChange={handleChange}
                        className="form-input w-full"
                        aria-required="false"
                      />
                    </div>

                    <div>
                      <label htmlFor="message" className="block text-sm font-medium text-neutral/80 mb-1">
                        Message <span className="text-accent">*</span>
                      </label>
                      <textarea
                        id="message"
                        name="message"
                        value={formData.message}
                        onChange={handleChange}
                        required
                        rows={4}
                        className="form-input w-full"
                        aria-required="true"
                      />
                    </div>

                    <button
                      type="submit"
                      className="btn-primary w-full"
                      disabled={isSubmitting}
                    >
                      {isSubmitting ? 'Submitting...' : 'Submit Form'}
                    </button>
                  </form>
                ) : (
                  <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -20 }}
                    className="text-center space-y-6 py-8"
                  >
                    <div className="flex justify-center">
                      <CheckCircle size={64} className="text-accent" />
                    </div>
                    <h3 className="text-2xl font-bold text-neutral">Thank You for Submitting!</h3>
                    <p className="text-neutral/80">We'll get back to you within 24 hours.</p>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Contact;